import { Buffer } from 'buffer';

globalThis.Buffer = Buffer;

import './prism-setup';
import './prism-languages';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FluentProvider } from '@fluentui/react-components';
import { AuthProvider } from './auth/AuthProvider';
import { lightTheme } from './styles/theme';
import { useThemeStore } from '@stoplight/mosaic';
// `@stoplight/elements/styles.min.css` and all other third-party assets are
// bundled locally by Vite from `node_modules` — they are not loaded from an
// external CDN. Subresource Integrity (SRI) is therefore enforced by the SWA
// edge + content-hash discipline rather than per-tag `integrity=` attributes.
// See `index.html` for the full SRI strategy decision.
import '@stoplight/elements/styles.min.css';
import './styles/global.css';
import { initializeTelemetry } from './lib/telemetry';
import { App } from './App';

// Initialize client-side telemetry sink before rendering. No-ops in local dev
// when `VITE_APPINSIGHTS_CONNECTION_STRING` is unset.
initializeTelemetry();

// Override Mosaic's default blue primary with Foxit orange so that
// computeTheme generates orange CSS when MosaicProvider mounts.
useThemeStore.getState().setColor('primary', 'hsl(21, 100%, 50%)');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <BrowserRouter>
      <FluentProvider theme={lightTheme}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <App />
          </AuthProvider>
        </QueryClientProvider>
      </FluentProvider>
    </BrowserRouter>
  </StrictMode>,
);

